import {
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    Typography,
    Box,
    TextField,
    InputAdornment, ListSubheader,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { getOptions } from "../../misc/widget-functions"
import InfoIcon from "../../components/general/infoIcon"

export const widgetFilterableSelectPrototype = [
    {
        key: "infotext",
        type: "STRING",
        label: "infotext",
        size: 6,
    },
]

const FilterableSelectWidget = ({
                                    widget,
                                    value,
                                    onValueChange,
                                    disabled,
                                }) => {
    const [searchTerm, setSearchTerm] = useState("")

    const options = useMemo(() => getOptions(widget) ?? [], [widget])

    const filteredOptions = useMemo(() => {
        if (!searchTerm.trim()) return options
        return options.filter((option) =>
            option?.label?.toLowerCase().includes(searchTerm.toLowerCase())
        )
    }, [options, searchTerm])

    const onSelectChange = (event) => {
        const selectedVal = event.target.value
        const newValue = options.find((option) => option.value === selectedVal)

        if (onValueChange && newValue) {
            onValueChange(newValue.value)
        }
    }

    return (
        <FormControl fullWidth>
            {(widget.text || widget.infotext) && (
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    {widget.text && (
                        <Typography className={disabled ? "disabled" : ""}>
                            {widget.text}
                        </Typography>
                    )}
                    {widget.infotext && <InfoIcon infoText={widget.infotext} />}
                </Box>
            )}

            <Select
                id={widget.id}
                key={widget.id}
                value={value ?? ""}
                onChange={onSelectChange}
                className={widget.className}
                disabled={disabled}
                // Reset search query when dropdown closes
                onClose={() => setSearchTerm("")}
                // Prevents Menu from focusing option index 0 on open so typing goes straight to search
                MenuProps={{
                    autoFocus: false,
                    PaperProps: {
                        style: { maxHeight: 350 },
                    },
                }}
            >
                <ListSubheader
                    sx={{ pt: 1, pb: 1, focusVisible: { outline: "none" } }}
                    onKeyDown={(e) => e.stopPropagation()} // Stop Select hotkeys from triggering while typing space/letters
                >
                    <TextField
                        size="small"
                        autoFocus
                        placeholder="Search..."
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            // Allow spacebar typing without closing or selecting items
                            if (e.key !== "Escape") {
                                e.stopPropagation()
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </ListSubheader>

                {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => (
                        <MenuItem
                            key={option?.id || option?.value}
                            value={option.value}
                            disabled={option?.disabled}
                        >
                            {option?.label}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled>No options found</MenuItem>
                )}
            </Select>

            {widget.helperText && (
                <FormHelperText>{widget.helperText}</FormHelperText>
            )}
        </FormControl>
    )
}

FilterableSelectWidget.propTypes = {
    widget: PropTypes.object.isRequired,
    value: PropTypes.string,
    onValueChange: PropTypes.func,
    disabled: PropTypes.bool,
}

export default FilterableSelectWidget